import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import Navigation from "../components/Navigation";
import { apiFetch } from "../utils/api";
import { SERVICE_MODES, getServiceModeLabel } from "../constants/serviceModes";

interface VerificationLink {
  id: number;
  token: string;
  url: string;
  branch_id?: string;
  merchant_id: string;
  verification_type: string;
  service_mode?: string;
  created_at: string;
  expires_at: string;
  is_active: boolean;
  creator_id?: number;
  creator_email?: string;
}

const Links: React.FC = () => {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [links, setLinks] = useState<VerificationLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verificationType, setVerificationType] = useState<"kyc" | "yesno">(
    "kyc",
  );
  const [serviceMode, setServiceMode] = useState("");
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState<number | null>(null);
  const [editingLinkType, setEditingLinkType] = useState<"kyc" | "yesno">(
    "kyc",
  );
  const [editingServiceMode, setEditingServiceMode] = useState("");
  const [linkHasVerification, setLinkHasVerification] = useState<{
    [key: number]: boolean;
  }>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "kyc" | "yesno">("all");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchLinks();
  }, [user]);

  useEffect(() => {
    // Check which links have verifications
    const checkLinkVerifications = async () => {
      const hasVerificationMap: { [key: number]: boolean } = {};
      for (const link of links) {
        try {
          const response = await apiFetch("/verifications");
          if (response.ok) {
            const data = await response.json();
            const hasVerification = data.verifications.some(
              (v: any) => v.link_token === link.token,
            );
            hasVerificationMap[link.id] = hasVerification;
          }
        } catch (err) {
          hasVerificationMap[link.id] = false;
        }
      }
      setLinkHasVerification(hasVerificationMap);
    };
    if (links.length > 0) {
      checkLinkVerifications();
    }
  }, [links]);

  const fetchLinks = async () => {
    try {
      const response = await apiFetch("/links");

      if (!response.ok) throw new Error("Failed to fetch links");

      const data = await response.json();
      setLinks(data.links);
    } catch (err: any) {
      // Don't set error for session expiration as user is being logged out
      if (err.message !== "Session expired. Please log in again.") {
        setError(err.message);
      }
    }
  };

  const generateLink = async () => {
    if (!serviceMode) {
      setError("Please select a mode");
      showToast("Please select a mode", "error");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiFetch("/links", {
        method: "POST",
        body: JSON.stringify({
          verification_type: verificationType,
          service_mode: serviceMode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate link");
      }

      const data = await response.json();
      setLinks([data.link, ...links]);
      setShowLinkForm(false);
      setVerificationType("kyc");
      setServiceMode("");
      showToast("Verification link generated successfully!", "success");
      fetchLinks();
    } catch (err: any) {
      if (err.message !== "Session expired. Please log in again.") {
        setError(err.message);
        showToast(err.message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast("Link copied to clipboard!", "success");
  };

  const handleEditLink = (link: VerificationLink) => {
    setEditingLinkId(link.id);
    setEditingLinkType(link.verification_type as "kyc" | "yesno");
    setEditingServiceMode(link.service_mode || "");
  };

  const handleUpdateLink = async (linkId: number) => {
    if (!editingServiceMode) {
      showToast("Please select a mode", "error");
      return;
    }

    try {
      const response = await apiFetch(`/links/${linkId}`, {
        method: "PUT",
        body: JSON.stringify({
          verification_type: editingLinkType,
          service_mode: editingServiceMode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update link");
      }

      showToast("Link updated successfully!", "success");
      setEditingLinkId(null);
      fetchLinks();
    } catch (err: any) {
      if (err.message !== "Session expired. Please log in again.") {
        showToast(err.message, "error");
      }
    }
  };

  const handleDeleteLink = async (linkId: number) => {
    if (!window.confirm("Are you sure you want to delete this link?")) {
      return;
    }

    try {
      const response = await apiFetch(`/links/${linkId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete link");
      }

      showToast("Link deleted successfully!", "success");
      fetchLinks();
    } catch (err: any) {
      if (err.message !== "Session expired. Please log in again.") {
        showToast(err.message, "error");
      }
    }
  };

  return (
    <div className="dashboard-container">
      <Navigation onLogout={logout} />
      <div className="dashboard-content">
        <div className="header">
          <h1 className="title">Verification Links</h1>
          <p className="subtitle">
            {user?.role === "admin"
              ? "Manage all verification links"
              : "Manage your verification links"}
          </p>
        </div>

        <div className="dashboard-actions">
          <button
            className="verify-button"
            onClick={() => setShowLinkForm(!showLinkForm)}
            disabled={loading}
          >
            {showLinkForm ? "Cancel" : "Generate Verification Link"}
          </button>
        </div>

        {showLinkForm && (
          <div className="dashboard-section">
            <h2>Generate Verification Link</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                generateLink();
              }}
            >
              <div className="input-container">
                <label className="label" htmlFor="verificationType">
                  Verification Type
                </label>
                <select
                  id="verificationType"
                  className="input"
                  value={verificationType}
                  onChange={(e) =>
                    setVerificationType(e.target.value as "kyc" | "yesno")
                  }
                >
                  <option value="kyc">KYC Verification</option>
                  <option value="yesno">YES/NO Verification</option>
                </select>
              </div>
              <div className="input-container">
                <label className="label" htmlFor="serviceMode">
                  Mode
                </label>
                <select
                  id="serviceMode"
                  className="input"
                  value={serviceMode}
                  onChange={(e) => setServiceMode(e.target.value)}
                  required
                >
                  <option value="">SELECT ONE</option>
                  {SERVICE_MODES.map((mode) => (
                    <option key={mode.code} value={mode.code}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className="verify-button"
                disabled={loading}
              >
                {loading ? "Generating..." : "Generate Link"}
              </button>
            </form>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="dashboard-section">
          <h2>All Links</h2>

          {/* Search and Filter Controls */}
          <div
            style={{
              marginBottom: "24px",
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              placeholder="Search by URL or creator email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="input"
              style={{ flex: "1", minWidth: "200px", maxWidth: "400px" }}
            />
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as "all" | "kyc" | "yesno");
                setCurrentPage(1);
              }}
              className="input"
              style={{ width: "auto", padding: "8px 12px" }}
            >
              <option value="all">All Types</option>
              <option value="kyc">KYC</option>
              <option value="yesno">YES/NO</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(
                  e.target.value as "all" | "active" | "inactive",
                );
                setCurrentPage(1);
              }}
              className="input"
              style={{ width: "auto", padding: "8px 12px" }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {(() => {
            // Filter links
            const filteredLinks = links.filter((link) => {
              const matchesSearch =
                searchTerm === "" ||
                link.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (link.creator_email &&
                  link.creator_email
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()));

              const matchesType =
                filterType === "all" || link.verification_type === filterType;

              const isActive =
                link.is_active && new Date(link.expires_at) > new Date();
              const matchesStatus =
                filterStatus === "all" ||
                (filterStatus === "active" && isActive) ||
                (filterStatus === "inactive" && !isActive);

              return matchesSearch && matchesType && matchesStatus;
            });

            // Pagination
            const totalPages = Math.ceil(filteredLinks.length / itemsPerPage);
            const startIndex = (currentPage - 1) * itemsPerPage;
            const paginatedLinks = filteredLinks.slice(
              startIndex,
              startIndex + itemsPerPage,
            );

            return (
              <>
                {filteredLinks.length === 0 ? (
                  <div className="empty-state">
                    <p>
                      {links.length === 0
                        ? 'No verification links generated yet. Click "Generate Verification Link" to create one.'
                        : "No links match your search criteria."}
                    </p>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        marginBottom: "16px",
                        color: "#666",
                        fontSize: "0.9rem",
                      }}
                    >
                      Showing {startIndex + 1}-
                      {Math.min(
                        startIndex + itemsPerPage,
                        filteredLinks.length,
                      )}{" "}
                      of {filteredLinks.length} links
                    </div>
                    <div>
                      {paginatedLinks.map((link) => {
                        const hasVerification =
                          linkHasVerification[link.id] || false;
                        const canEdit = !hasVerification;

                        return (
                          <div key={link.id} className="link-card">
                            <div className="link-header">
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link-url"
                              >
                                {link.url}
                              </a>
                              <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                  onClick={() => copyToClipboard(link.url)}
                                  className="link-copy-btn"
                                >
                                  Copy
                                </button>
                                {canEdit && (
                                  <>
                                    {editingLinkId === link.id ? (
                                      <>
                                        <select
                                          className="input"
                                          value={editingLinkType}
                                          onChange={(e) =>
                                            setEditingLinkType(
                                              e.target.value as "kyc" | "yesno",
                                            )
                                          }
                                          style={{
                                            width: "auto",
                                            padding: "6px 12px",
                                          }}
                                        >
                                          <option value="kyc">KYC</option>
                                          <option value="yesno">YES/NO</option>
                                        </select>
                                        <select
                                          className="input"
                                          value={editingServiceMode}
                                          onChange={(e) =>
                                            setEditingServiceMode(
                                              e.target.value,
                                            )
                                          }
                                          style={{
                                            width: "auto",
                                            padding: "6px 12px",
                                          }}
                                        >
                                          <option value="">SELECT ONE</option>
                                          {SERVICE_MODES.map((mode) => (
                                            <option
                                              key={mode.code}
                                              value={mode.code}
                                            >
                                              {mode.label}
                                            </option>
                                          ))}
                                        </select>
                                        <button
                                          className="link-copy-btn"
                                          onClick={() =>
                                            handleUpdateLink(link.id)
                                          }
                                          style={{ background: "#004a9c" }}
                                        >
                                          Save
                                        </button>
                                        <button
                                          className="link-copy-btn"
                                          onClick={() => setEditingLinkId(null)}
                                          style={{ background: "#6c757d" }}
                                        >
                                          Cancel
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className="link-copy-btn"
                                          onClick={() => handleEditLink(link)}
                                          style={{ background: "#004a9c" }}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          className="link-copy-btn"
                                          onClick={() =>
                                            handleDeleteLink(link.id)
                                          }
                                          style={{ background: "#dc3545" }}
                                        >
                                          Delete
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="link-meta">
                              <div className="link-meta-item">
                                <span className="link-meta-label">Created</span>
                                <span>
                                  {new Date(link.created_at).toLocaleString()}
                                </span>
                              </div>
                              <div className="link-meta-item">
                                <span className="link-meta-label">Expires</span>
                                <span>
                                  {new Date(link.expires_at).toLocaleString()}
                                </span>
                              </div>
                              {link.branch_id && (
                                <div className="link-meta-item">
                                  <span className="link-meta-label">
                                    Branch ID
                                  </span>
                                  <span>{link.branch_id}</span>
                                </div>
                              )}
                              <div className="link-meta-item">
                                <span className="link-meta-label">Type</span>
                                <span>
                                  {link.verification_type === "yesno"
                                    ? "YES/NO Verification"
                                    : "KYC Verification"}
                                </span>
                              </div>
                              {link.service_mode && (
                                <div className="link-meta-item">
                                  <span className="link-meta-label">Mode</span>
                                  <span>
                                    {getServiceModeLabel(link.service_mode)}
                                  </span>
                                </div>
                              )}
                              <div className="link-meta-item">
                                <span className="link-meta-label">Status</span>
                                <span
                                  className={`link-status ${
                                    link.is_active &&
                                    new Date(link.expires_at) > new Date()
                                      ? "active"
                                      : "inactive"
                                  }`}
                                >
                                  {link.is_active &&
                                  new Date(link.expires_at) > new Date()
                                    ? "Active"
                                    : "Inactive"}
                                </span>
                              </div>
                              {user?.role === "admin" && link.creator_email && (
                                <div className="link-meta-item">
                                  <span className="link-meta-label">
                                    Created By
                                  </span>
                                  <span>{link.creator_email}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "12px",
                          marginTop: "24px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          className="verify-button"
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={currentPage === 1}
                          style={{
                            background: currentPage === 1 ? "#ccc" : "#004a9c",
                            cursor:
                              currentPage === 1 ? "not-allowed" : "pointer",
                            width: "auto",
                            padding: "8px 16px",
                          }}
                        >
                          Previous
                        </button>
                        <span style={{ fontSize: "0.9rem", color: "#666" }}>
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          className="verify-button"
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(totalPages, prev + 1),
                            )
                          }
                          disabled={currentPage === totalPages}
                          style={{
                            background:
                              currentPage === totalPages ? "#ccc" : "#004a9c",
                            cursor:
                              currentPage === totalPages
                                ? "not-allowed"
                                : "pointer",
                            width: "auto",
                            padding: "8px 16px",
                          }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default Links;
